'use client';

import React from 'react';
import styles from './footer.module.scss';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useServiceVisibility } from '@/hooks/useServiceVisibility';

const CONTACT_URL = '/contact-us';

const PRODUCT_LINKS: { id: string; label: string; href: string }[] = [
  { id: 'specific-yield', label: 'Specific Yield', href: '/peatsense?source=specific-yield' },
  { id: 'pfi-scorecard', label: 'Emission Scoping Calculator', href: '/peatsense?source=pfi-scorecard' },
  { id: 'maps', label: 'Maps', href: '/map' },
  { id: 'sentinel', label: 'Sentinel-2 Imagery', href: '/peatsense?source=sentinel' },
  { id: 'sentinel1', label: 'Sentinel-1 Imagery', href: '/peatsense?source=sentinel1' },
  { id: 'safe-to-geotiff', label: 'SAFE to GeoTIFF', href: '/peatsense?source=safe-to-geotiff' },
  { id: 'pixel-extraction', label: 'S2 Extraction Service', href: '/peatsense?source=pixel-extraction' },
  { id: 'sentinel2-wrapper', label: 'Sentinel-2 Wrapper', href: '/peatsense?source=sentinel2-wrapper' },
  { id: 'pixel-annotation', label: 'Pixel Annotation & Cloud Masking', href: '/peatsense?source=pixel-annotation' },
  { id: 'landcover-inference', label: 'Model Inference', href: '/peatsense?source=landcover-inference' },
  { id: 'radiometric-correction', label: 'Radiometric Correction', href: '/peatsense?source=radiometric-correction' },
  { id: 'parquet-viewer', label: 'Files Reader', href: '/peatsense?source=parquet-viewer' },
];

function Footer() {
  const router = useRouter();
  const { isOffered, hasAccess } = useServiceVisibility();

  const visibleProductLinks = PRODUCT_LINKS.filter((p) => isOffered(p.id));

  const handleProductLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string,
    href: string,
  ) => {
    e.preventDefault();
    router.push(hasAccess(id) ? href : CONTACT_URL);
  };

  return (
    <footer className={styles.footerContainer}>
      <div className={styles.footerTop}>

        <div className={styles.brandCol}>
          <Link href="/" className={styles.brandWrapper}>
            <Image
              src="/logo_black.svg"
              priority
              width={0}
              height={0}
              sizes="100vw"
              className={styles.brandLogo}
              alt="AI2Peat"
            />
            <span className={styles.brandName}>AI2Peat</span>
          </Link>
          <p className={styles.tagline}>
            Advanced peatland monitoring for a sustainable future.
          </p>
        </div>

        <div className={styles.footerCol}>
          <span className={styles.colTitle}>Products</span>
          {visibleProductLinks.map((p) => (
            <Link
              key={p.id}
              href={p.href}
              className={styles.footerLink}
              onClick={(e) => handleProductLinkClick(e, p.id, p.href)}
            >
              {p.label}
            </Link>
          ))}
          {/* <Link href="/directory" className={styles.footerLink}>Directory</Link> */}
        </div>

        <div className={styles.footerCol}>
          <span className={styles.colTitle}>Additional Resources</span>
          <Link href="/knowledge-hub" className={styles.footerLink}>Knowledge Hub</Link>
          <Link href="/glossary" className={styles.footerLink}>Glossary</Link>
          <Link href="/news" className={styles.footerLink}>News</Link>
        </div>

        <div className={styles.footerCol}>
          <span className={styles.colTitle}>AI2Peat</span>
          <Link href="/about-us" className={styles.footerLink}>About Us</Link>
          <Link href="/acknowledgements" className={styles.footerLink}>Acknowledgements</Link>
          <Link href="/faq-peatsense" className={styles.footerLink}>FAQ</Link>
          <Link href="/careers" className={styles.footerLink}>Careers</Link>
        </div>

      </div>

      <hr className={styles.footerDivider} />

      <div className={styles.footerBottom}>
        The AI2Peat project is funded by Taighde Éireann – Research Ireland under the National Challenge Fund - Future Digital Challenge (Grant Number: 22/NCF/FD/10969)
      </div>
    </footer>
  );
}

export default Footer;
